<template>
  <div>
    <h2 class="mb-2 text-xl">{{ wt('projectSubmission', 'title') }}</h2>
    <HelpText slug="project_summary_submission" />
    <p class="mb-4 text-sm text-neutral-450">{{ wt('projectSubmission', 'description') }}</p>

    <SubmissionPlanningStart
      v-model:form="form.planningStart"
      :validRoles="validRoles"
      :country="country"
      :errors="errors" />

    <SubmissionPlanningEnd
      v-model:form="form.planningEnd"
      :validRoles="validRoles"
      :country="country"
      :planningStartRoleData="form.planningStart.roleData"
      :errors="errors" />

    <SubmissionFooter
      v-model:consent="form.consent"
      :submitting="submitting"
      :submitError="submitError"
      :errors="errors"
      @submit="onSubmit" />

    <SubmissionSuccessModal
      :visible="showModal"
      :formData="modalFormSnapshot!"
      :metadata="modalMetadata"
      @close="showModal = false" />
  </div>
</template>

<script setup lang="ts">
import { useCoordination } from '@/helpers/coordination'
import { reactive, ref, computed, nextTick } from 'vue'
import { useProjectStore } from '@/stores/project'
import HelpText from '@/components/partials/display/HelpText.vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import { http } from '@/services/api'
import { Events, useEventBus } from '@/helpers/event-bus'
import { scrollToElement } from '@/helpers/shared'
import SubmissionPlanningStart from './SubmissionPlanningStart.vue'
import SubmissionPlanningEnd from './SubmissionPlanningEnd.vue'
import SubmissionFooter from './SubmissionFooter.vue'
import SubmissionSuccessModal from './SubmissionSuccessModal.vue'
import type { SubmissionFormSnapshot, SubmissionMetadata } from '@/services/pdf/pdfmake-submission-generator'
import * as Sentry from '@sentry/vue'
import { trackFeedbackSent } from '@/services/analytics/plausible'

const { wt } = useWidgetText('widget-texts-project-summary')
const eventBus = useEventBus()

const projectStore = useProjectStore()
const project = projectStore.getProject

const { enabled: coordinationEnabled } = useCoordination()
const validRoles = computed(() =>
  projectStore.getValidRoles.filter((role) => role !== 'SPECIALIST_COORDINATION' || !coordinationEnabled.value)
)
const country = computed(() => (project?.general?.data?.country as string) ?? '')

const projectTitle = computed(() => {
  const meta = projectStore.getMeta
  return ((meta as Record<string, unknown> | null)?.title as string) ?? ''
})

function buildInitialPlanningStartRoleDataRecord(): Record<
  string,
  {
    difficultyFactor: number | null
    effortDeterminingCosts: number | null
    serviceShare: number | null
    offeredHours: number | null
  }
> {
  const record: Record<
    string,
    {
      difficultyFactor: number | null
      effortDeterminingCosts: number | null
      serviceShare: number | null
      offeredHours: number | null
    }
  > = {}
  for (const role of validRoles.value) {
    record[role] = {
      difficultyFactor: 1,
      effortDeterminingCosts: null,
      serviceShare: null,
      offeredHours: null,
    }
  }
  return record
}

function buildInitialPlanningEndRoleDataRecord(): Record<
  string,
  {
    difficultyFactor: number | null
    effortDeterminingCosts: number | null
    serviceShare: number | null
    invoicedHours: number | null
    effectiveHours: number | null
  }
> {
  const record: Record<
    string,
    {
      difficultyFactor: number | null
      effortDeterminingCosts: number | null
      serviceShare: number | null
      invoicedHours: number | null
      effectiveHours: number | null
    }
  > = {}
  for (const role of validRoles.value) {
    record[role] = {
      difficultyFactor: null,
      effortDeterminingCosts: null,
      serviceShare: null,
      invoicedHours: null,
      effectiveHours: null,
    }
  }
  return record
}

const form = reactive({
  planningStart: {
    year: null as number | null,
    constructionCosts: null as number | null,
    roleData: buildInitialPlanningStartRoleDataRecord(),
  },
  planningEnd: {
    year: null as number | null,
    constructionCosts: null as number | null,
    roleData: buildInitialPlanningEndRoleDataRecord(),
  },
  consent: false,
})

const errors = ref<Record<string, string>>({})
const submitting = ref(false)
const submitError = ref(false)
const showModal = ref(false)
const modalFormSnapshot = ref<SubmissionFormSnapshot | null>(null)

const modalMetadata = computed<SubmissionMetadata>(() => ({
  projectTitle: projectTitle.value,
  validRoles: validRoles.value,
  country: country.value,
}))

function resetForm() {
  form.planningStart.year = null
  form.planningStart.constructionCosts = null
  form.planningStart.roleData = buildInitialPlanningStartRoleDataRecord()
  form.planningEnd.year = null
  form.planningEnd.constructionCosts = null
  form.planningEnd.roleData = buildInitialPlanningEndRoleDataRecord()
  form.consent = false
  errors.value = {}
}

function validate(): boolean {
  errors.value = {}

  const currentYear = new Date().getFullYear()

  if (!form.planningStart.year) {
    errors.value['planningStart.year'] = 'Pflichtfeld'
  } else if (form.planningStart.year < 1800 || form.planningStart.year > currentYear) {
    errors.value['planningStart.year'] = `1800-${currentYear}`
  }

  for (const role of validRoles.value) {
    if (form.planningStart.roleData[role]?.difficultyFactor == null) {
      errors.value[`planningStart.difficultyFactor.${role}`] = 'Pflichtfeld'
    }
    if (!form.planningStart.roleData[role]?.effortDeterminingCosts) {
      errors.value[`planningStart.effortDeterminingCosts.${role}`] = 'Pflichtfeld'
    }
    const share = form.planningStart.roleData[role]?.serviceShare
    if (share != null && (share < 0 || share > 100)) {
      errors.value[`planningStart.serviceShare.${role}`] = '0-100'
    }
  }

  // Planning end year: optional, but if provided must be valid
  if (form.planningEnd.year != null) {
    if (form.planningEnd.year < 1800 || form.planningEnd.year > currentYear) {
      errors.value['planningEnd.year'] = `1800-${currentYear}`
    } else if (form.planningStart.year && form.planningEnd.year < form.planningStart.year) {
      errors.value['planningEnd.year'] = `Ab ${form.planningStart.year}`
    }
  }

  // Planning end: optional, but validate service share range if provided
  for (const role of validRoles.value) {
    const share = form.planningEnd.roleData[role]?.serviceShare
    if (share != null && (share < 0 || share > 100)) {
      errors.value[`planningEnd.serviceShare.${role}`] = '0-100'
    }
  }

  if (!form.consent) {
    errors.value['consent'] = 'Pflichtfeld'
  }

  return Object.keys(errors.value).length === 0
}

function buildSubmissionPayload(): Record<string, unknown> {
  return {
    country: country.value,
    quantityArea: (project?.general?.data?.quantityArea as number) ?? 0,
    planningStart: {
      year: form.planningStart.year,
      constructionCosts: form.planningStart.constructionCosts,
      roleData: Object.fromEntries(validRoles.value.map((role) => [role, { ...form.planningStart.roleData[role] }])),
    },
    planningEnd: {
      year: form.planningEnd.year,
      constructionCosts: form.planningEnd.constructionCosts,
      roleData: Object.fromEntries(
        validRoles.value.map((role) => [
          role,
          {
            ...form.planningEnd.roleData[role],
            difficultyFactor:
              form.planningEnd.roleData[role].difficultyFactor ?? form.planningStart.roleData[role].difficultyFactor,
          },
        ])
      ),
    },
  }
}

function buildAnonymizedMeta(): Record<string, unknown> | null {
  const rawMeta = projectStore.getMeta
  if (!rawMeta) return null
  const meta = { ...rawMeta }
  delete meta.userId
  return meta
}

function buildAnonymizedProject(): Record<string, unknown> | null {
  const rawProject = projectStore.getProject
  if (!rawProject) return null
  const project = JSON.parse(JSON.stringify(rawProject))
  if (project?.general?.data) {
    delete project.general.data.street
    delete project.general.data.houseNumber
    delete project.general.data.zip
    delete project.general.data.city
  }
  return project
}

function captureFormSnapshot(): SubmissionFormSnapshot {
  return JSON.parse(
    JSON.stringify({
      planningStart: form.planningStart,
      planningEnd: {
        ...form.planningEnd,
        roleData: Object.fromEntries(
          validRoles.value.map((role) => [
            role,
            {
              ...form.planningEnd.roleData[role],
              difficultyFactor:
                form.planningEnd.roleData[role].difficultyFactor ?? form.planningStart.roleData[role].difficultyFactor,
            },
          ])
        ),
      },
    })
  )
}

async function onSubmit() {
  if (!validate()) {
    eventBus.emit(Events.SHOW_ERROR, { key: 'form.error.invalid' })
    nextTick(() => {
      const firstError = document.querySelector('.core-form-error')
      if (firstError) {
        scrollToElement(firstError)
      }
    })
    return
  }

  submitting.value = true
  submitError.value = false

  try {
    const data = buildSubmissionPayload()
    const meta = buildAnonymizedMeta()
    const project = buildAnonymizedProject()

    // Capture snapshot before clearing
    modalFormSnapshot.value = captureFormSnapshot()

    await http.post('/api/project-submissions', { data, meta, project })
    trackFeedbackSent()

    // Show modal and clear form
    showModal.value = true
    resetForm()
  } catch (error) {
    submitError.value = true
    modalFormSnapshot.value = null
    if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
      console.error('[ProjectSubmissionForm]', error)
    }
    Sentry.captureException(error)
  } finally {
    submitting.value = false
  }
}
</script>
